import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import {
  ICliente,
  IFilter,
  ILayerMapaPersonalizado,
  IPopulate,
  IQueryParam,
} from 'modelos/src';
import {
  IAlerta,
  IEvento,
  ILocalidad,
  IPunto,
  ISirena,
  IZona,
} from 'modelosSeguridad/src';
import { from, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
} from 'rxjs/operators';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { MapaFacilV2Service } from '../../../../auxiliares/mapbox/mapa-facil-v2.service';
import { MapboxDrawService } from '../../../../auxiliares/mapbox/mapbox-draw.service';
import { IntegracionesService } from '../../../../auxiliares/servicios/http/integraciones.service';
import { TipoMapaBase } from '../../../standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { LoginService } from '../../../login/login.service';
import { ZonasSeguridadLayerManager } from '../../../../auxiliares/mapbox/entity-managers/zonas-seguridad-layer.manager';
import { LocalidadesSeguridadLayerManager } from '../../../../auxiliares/mapbox/entity-managers/localidades-seguridad-layer.manager';
import {
  FiltroTemporal,
  FlotanteAlertasSeguridadComponent,
  IResumenPoligono,
  RangoTemporal,
} from './flotante-alertas-seguridad/flotante-alertas-seguridad.component';

const POLLING_SECONDS = 30;
const STORAGE_KEY = 'mapaAlertasSeguridadConfig';

interface IPersistedConfig {
  tiposActivos?: string[];
  filtroTemporal?: FiltroTemporal;
  rangoPersonalizado?: RangoTemporal;
  soloActivas?: boolean;
}

@Component({
  selector: 'app-mapa-alertas-seguridad',
  templateUrl: './mapa-alertas-seguridad.component.html',
  styleUrl: './mapa-alertas-seguridad.component.scss',
  standalone: false,
})
export class MapaAlertasSeguridadComponent implements OnInit, OnDestroy {
  private lista2Service = inject(Lista2Service);
  private mapaFacilV2Service = inject(MapaFacilV2Service);
  private drawService = inject(MapboxDrawService);
  private integracionesService = inject(IntegracionesService);
  private zonasManager = inject(ZonasSeguridadLayerManager);
  private localidadesManager = inject(LocalidadesSeguridadLayerManager);

  public loading = false;

  public alertas: IAlerta[] = [];
  public sirenas: ISirena[] = [];
  public zonas: IZona[] = [];
  public localidades: ILocalidad[] = [];
  public puntos: IPunto[] = [];
  public eventos: IEvento[] = [];

  private alertas$?: Subscription;
  private sirenas$?: Subscription;
  private zonas$?: Subscription;
  private localidades$?: Subscription;
  private puntos$?: Subscription;
  private eventos$?: Subscription;

  public tiposActivos = new Set<string>([
    'alertas',
    'sirenas',
    'zonas',
    'localidades',
    'puntos',
    'eventos',
  ]);

  public filtroTemporal: FiltroTemporal = '7d';
  public rangoPersonalizado?: RangoTemporal;
  public soloActivas = true;

  private loadConfig(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const cfg: IPersistedConfig = JSON.parse(raw);
      if (Array.isArray(cfg.tiposActivos)) {
        this.tiposActivos = new Set(cfg.tiposActivos);
      }
      if (cfg.filtroTemporal) this.filtroTemporal = cfg.filtroTemporal;
      if (cfg.rangoPersonalizado)
        this.rangoPersonalizado = cfg.rangoPersonalizado;
      if (typeof cfg.soloActivas === 'boolean')
        this.soloActivas = cfg.soloActivas;
    } catch {
      // ignore
    }
  }

  private saveConfig(): void {
    const cfg: IPersistedConfig = {
      tiposActivos: Array.from(this.tiposActivos),
      filtroTemporal: this.filtroTemporal,
      rangoPersonalizado: this.rangoPersonalizado,
      soloActivas: this.soloActivas,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
    } catch {
      // ignore
    }
  }

  // Polígono
  public dibujandoPoligono = false;
  public buscandoPoligono = false;
  public resumenPoligono: IResumenPoligono | null = null;
  private poligono$?: Subscription;
  private drawInicializado = false;
  private resultadosPoligonoBeforeDrawing: {
    alertas: IAlerta[];
    sirenas: ISirena[];
  } | null = null;

  @ViewChild(FlotanteAlertasSeguridadComponent)
  private flotante?: FlotanteAlertasSeguridadComponent;

  public clientePropio: ICliente = LoginService.getClientePropio();
  public mapasPersonalizados: ILayerMapaPersonalizado[] = [];
  public readonly TIPO_MAPA_BASE_KEY = 'tipoMapaBaseAlertasSeguridadComponent';
  public tipoMapaBase: TipoMapaBase =
    (localStorage.getItem(this.TIPO_MAPA_BASE_KEY) as TipoMapaBase) || 'osm';

  public allowedTypes: string[] = ['alertasSeguridad', 'eventosSeguridad'];

  private map: mapboxgl.Map | null = null;

  // ===== Listados con polling =====

  private alertaPopulate: IPopulate[] = [
    { path: 'configVecino', select: 'datosPersonales' },
    { path: 'boton', select: 'nombre color' },
    { path: 'categoria', select: 'nombre' },
  ];

  private buildAlertaQuery(): IQueryParam {
    const filter: IFilter<IAlerta> = {};
    if (this.soloActivas) {
      // Activas: estado actual abierto, sin importar fecha histórica
      (filter as any).estadoActual = {
        $in: ['Sin Tratamiento', 'Liberada'],
      };
    } else {
      const { desde, hasta } = this.calcularRangoFechas();
      (filter as any).fechaCreacion = {
        $gte: desde.toISOString(),
        $lte: hasta.toISOString(),
      };
    }
    return {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(this.alertaPopulate),
      includeChildren: true,
      excludeTotalCount: true,
    };
  }

  private buildEventoQuery(): IQueryParam {
    const { desde, hasta } = this.calcularRangoFechas();
    const filter: IFilter<IEvento> = {
      fecha: {
        $gte: desde.toISOString(),
        $lte: hasta.toISOString(),
      } as any,
    };
    return {
      filter: JSON.stringify(filter),
      includeChildren: true,
      excludeTotalCount: true,
    };
  }

  private staticQuery: IQueryParam = {
    includeChildren: true,
    excludeTotalCount: true,
  };

  private calcularRangoFechas(): { desde: Date; hasta: Date } {
    if (this.filtroTemporal === 'custom' && this.rangoPersonalizado) {
      return {
        desde: new Date(this.rangoPersonalizado.desde),
        hasta: new Date(this.rangoPersonalizado.hasta),
      };
    }
    const dias =
      this.filtroTemporal === '1d' ? 1 : this.filtroTemporal === '2d' ? 2 : 7;
    const hasta = new Date();
    const desde = new Date();
    desde.setDate(desde.getDate() - dias);
    return { desde, hasta };
  }

  // ===== Suscripciones =====

  private listarAlertas(): void {
    this.alertas$?.unsubscribe();
    this.alertas$ = this.lista2Service
      .alertasSeguridad(this.buildAlertaQuery(), POLLING_SECONDS)
      .subscribe((data) => {
        this.alertas = data.datos;
        this.updateMap();
      });
  }

  private listarSirenas(): void {
    this.sirenas$?.unsubscribe();
    this.sirenas$ = this.lista2Service
      .sirenasSeguridad(this.staticQuery, POLLING_SECONDS * 2)
      .subscribe((data) => {
        this.sirenas = data.datos;
        this.updateMap();
      });
  }

  private listarZonas(): void {
    this.zonas$?.unsubscribe();
    this.zonas$ = this.lista2Service
      .zonasSeguridad(this.staticQuery, 0)
      .subscribe((data) => {
        this.zonas = data.datos;
        this.updateMap();
      });
  }

  private listarLocalidades(): void {
    this.localidades$?.unsubscribe();
    this.localidades$ = this.lista2Service
      .localidadesSeguridad(this.staticQuery, 0)
      .subscribe((data) => {
        this.localidades = data.datos;
        this.updateMap();
      });
  }

  private listarPuntos(): void {
    this.puntos$?.unsubscribe();
    this.puntos$ = this.lista2Service
      .puntosSeguridad(this.staticQuery, 0)
      .subscribe((data) => {
        this.puntos = data.datos;
        this.updateMap();
      });
  }

  private listarEventos(): void {
    this.eventos$?.unsubscribe();
    this.eventos$ = this.lista2Service
      .eventosSeguridad(this.buildEventoQuery(), POLLING_SECONDS)
      .subscribe((data) => {
        this.eventos = data.datos;
        this.updateMap();
      });
  }

  // ===== Sirenas: normalizar para mapa =====

  private sirenasParaMapa(): any[] {
    return this.sirenas
      .filter((s) => s.geojson?.coordinates?.length === 2)
      .map((s) => ({
        ...s,
        coordenadas: [s.geojson!.coordinates[0], s.geojson!.coordinates[1]] as [
          number,
          number,
        ],
      }));
  }

  // ===== Puntos: normalizar para mapa =====

  private puntosParaMapa(): any[] {
    return this.puntos
      .filter((p) => p.geojson?.coordinates?.length === 2)
      .map((p) => ({
        ...p,
        coordenadas: {
          lng: p.geojson!.coordinates[0],
          lat: p.geojson!.coordinates[1],
        },
      }));
  }

  // ===== Push de datos al mapa =====

  private updateMap(): void {
    if (!this.mapaFacilV2Service.isReady()) return;

    const alertasVisibles = this.tiposActivos.has('alertas')
      ? this.alertas
      : [];
    const sirenasVisibles = this.tiposActivos.has('sirenas')
      ? this.sirenasParaMapa()
      : [];
    const puntosVisibles = this.tiposActivos.has('puntos')
      ? this.puntosParaMapa()
      : [];
    const eventosVisibles = this.tiposActivos.has('eventos')
      ? this.eventos
      : [];

    this.mapaFacilV2Service.updateData({
      alertasSeguridad: alertasVisibles,
      sirenas: sirenasVisibles,
      puntos: puntosVisibles,
      eventosSeguridad: eventosVisibles,
    });

    this.zonasManager.updateZonas(
      this.tiposActivos.has('zonas') ? this.zonas : [],
    );
    this.localidadesManager.updateLocalidades(
      this.tiposActivos.has('localidades') ? this.localidades : [],
    );
  }

  // ===== Eventos del flotante =====

  public onTiposActivosChange(tipos: Set<string>): void {
    this.tiposActivos = tipos;
    this.updateMap();
    this.saveConfig();
  }

  public onFiltroTemporalChange(filtro: FiltroTemporal): void {
    this.filtroTemporal = filtro;
    if (filtro !== 'custom') {
      this.rangoPersonalizado = undefined;
      this.listarAlertas();
      this.listarEventos();
    }
    this.saveConfig();
  }

  public onRangoPersonalizadoChange(rango: RangoTemporal): void {
    this.rangoPersonalizado = rango;
    this.filtroTemporal = 'custom';
    this.listarAlertas();
    this.listarEventos();
    this.saveConfig();
  }

  public onSoloActivasChange(value: boolean): void {
    this.soloActivas = value;
    this.listarAlertas();
    this.saveConfig();
  }

  // ===== Polígono =====

  public iniciarPoligono(): void {
    if (!this.map) return;
    if (this.dibujandoPoligono) return;
    this.dibujandoPoligono = true;
    this.resumenPoligono = null;

    // Init lazy una sola vez (mapbox-draw rompe si se inicializa 2 veces)
    if (!this.drawInicializado) {
      try {
        this.drawService.initDrawControls(this.map);
        this.drawInicializado = true;
      } catch (err: any) {
        // Si el source ya existe (init previo en otra ruta), seguir adelante
        if (String(err?.message || '').includes('already a source with ID')) {
          this.drawInicializado = true;
        } else {
          console.error('initDrawControls error:', err);
          this.dibujandoPoligono = false;
          return;
        }
      }
    } else {
      // Limpiar geometrías previas antes de re-dibujar
      try {
        this.drawService.borrarTodo();
      } catch {
        // ignore
      }
    }

    try {
      this.drawService.dibujar('Polygon');
    } catch (err) {
      console.error('drawService.dibujar error:', err);
      this.dibujandoPoligono = false;
      return;
    }

    // multiPolygon emite en CADA vértice (draw.update en direct_select). Sin
    // colapsar, eso genera un request por movimiento. debounce agrupa la edición
    // en una sola búsqueda, distinct evita el replay stale del BehaviorSubject al
    // re-suscribir, y switchMap descarta respuestas de búsquedas superadas.
    this.poligono$?.unsubscribe();
    this.poligono$ = this.drawService.multiPolygon
      .pipe(
        // multiPolygon: [number, number][][][] — tomar primer anillo del primer polígono
        map((coords) => coords?.[0]?.[0] as [number, number][] | undefined),
        filter(
          (ring): ring is [number, number][] => !!ring && ring.length >= 3,
        ),
        debounceTime(400),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap((ring) => {
          this.dibujandoPoligono = false;
          return from(this.buscarEnPoligono(ring));
        }),
      )
      .subscribe();
  }

  public cancelarPoligono(): void {
    this.dibujandoPoligono = false;
    this.poligono$?.unsubscribe();
    if (this.drawInicializado) {
      try {
        this.drawService.parar();
        this.drawService.borrarTodo();
      } catch {
        // ignore
      }
    }
  }

  private ultimoPoligono: {
    type: 'Polygon';
    coordinates: [number, number][][];
  } | null = null;
  public exportandoPoligono = false;

  public async exportarPoligono(): Promise<void> {
    if (!this.ultimoPoligono || this.exportandoPoligono) return;
    this.exportandoPoligono = true;
    try {
      const { desde, hasta } = this.calcularRangoFechas();
      await this.integracionesService.exportarDentroPoligonoSeguridad({
        poligono: this.ultimoPoligono,
        desde: desde.toISOString(),
        hasta: hasta.toISOString(),
      });
    } finally {
      this.exportandoPoligono = false;
    }
  }

  public limpiarPoligono(): void {
    this.resumenPoligono = null;
    this.dibujandoPoligono = false;
    this.ultimoPoligono = null;
    this.poligono$?.unsubscribe();
    if (this.drawInicializado) {
      try {
        this.drawService.borrarTodo();
      } catch {
        // ignore
      }
    }
    // Restaurar listados originales
    this.listarAlertas();
    this.listarSirenas();
  }

  private fitBoundsToRing(ring: [number, number][]): void {
    if (!this.map || ring.length === 0) return;
    let minLng = ring[0][0];
    let maxLng = ring[0][0];
    let minLat = ring[0][1];
    let maxLat = ring[0][1];
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    this.map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 60, duration: 600 },
    );
  }

  private async buscarEnPoligono(ring: [number, number][]): Promise<void> {
    this.buscandoPoligono = true;
    try {
      const poligono = {
        type: 'Polygon' as const,
        coordinates: [ring],
      };
      this.ultimoPoligono = poligono;
      // Salir de direct_select (vertex handles) → simple_select para vista limpia
      try {
        const draw: any = (this.drawService as any).draw;
        if (draw?.changeMode) draw.changeMode('simple_select');
      } catch {
        // ignore
      }
      this.fitBoundsToRing(ring);
      const { desde, hasta } = this.calcularRangoFechas();
      const resp = await this.integracionesService.dentroPoligonoSeguridad({
        poligono,
        desde: desde.toISOString(),
        hasta: hasta.toISOString(),
      });
      this.alertas = resp.alertas || [];
      this.sirenas = resp.sirenas || [];
      this.resumenPoligono = {
        alertas: resp.alertas?.length || 0,
        sirenas: resp.sirenas?.length || 0,
        vecinos: resp.vecinos?.length || 0,
        controles: resp.controles?.length || 0,
        truncados: resp.truncados,
      };
      this.updateMap();
    } catch (err) {
      console.error('Error en búsqueda dentro de polígono', err);
      this.resumenPoligono = null;
    } finally {
      this.buscandoPoligono = false;
    }
  }

  // ===== Lifecycle =====

  public onMapReady(map: mapboxgl.Map): void {
    this.map = map;
    this.zonasManager.initialize(map);
    this.localidadesManager.initialize(map);
    this.updateMap();
  }

  async ngOnInit() {
    this.loading = true;
    this.loadConfig();
    if (this.clientePropio?.mapLayers) {
      this.mapasPersonalizados = this.clientePropio.mapLayers;
    }
    this.listarAlertas();
    this.listarSirenas();
    this.listarZonas();
    this.listarLocalidades();
    this.listarPuntos();
    this.listarEventos();
    this.loading = false;
  }

  ngOnDestroy() {
    this.alertas$?.unsubscribe();
    this.sirenas$?.unsubscribe();
    this.zonas$?.unsubscribe();
    this.localidades$?.unsubscribe();
    this.puntos$?.unsubscribe();
    this.eventos$?.unsubscribe();
    this.poligono$?.unsubscribe();
    this.zonasManager.cleanup();
    this.localidadesManager.cleanup();
    if (this.drawInicializado) {
      try {
        this.drawService.cleanup();
      } catch {
        // ignore
      }
      this.drawInicializado = false;
    }
    this.mapaFacilV2Service.clearAllData();
    void this.map;
    void this.flotante;
    void this.resultadosPoligonoBeforeDrawing;
  }
}
